import React, { useState, useEffect } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage';
import { GetAdminConfig, UpdateAdminConfig } from './../../../../utilities/BL/OptimoOpticas';

/**
 * Modal administrativo para la configuración integral de parámetros de campaña,
 * opciones visuales del pase digital y marcos regulatorios (T&C y Privacidad LFPDPPP).
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isOpen - Visibilidad del modal.
 * @param {Function} props.onClose - Callback al cerrar.
 * @param {Function} props.onConfigUpdated - Callback al persistir la nueva configuración.
 */
export function OptimoOpticasConfigModal({ isOpen, onClose, onConfigUpdated }) {
    const { showMessage } = useFloatMessage();

    // Estados de control
    const [activeTab, setActiveTab] = useState('campana'); // 'campana' | 'visualizacion' | 'legales'
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Estado del formulario
    const [formData, setFormData] = useState({
        matchTitle: 'Atlético de San Luis vs Rival',
        matchDate: '',
        campaignTitle: 'Rasca y Gana Optimo Ópticas',
        winnerMode: 'LIMITED', // 'LIMITED' | 'ALL'
        maxWinners: 50,
        couponPrefix: 'OPTI',
        discountDescription: 'Cupón válido por descuento especial en armazones y micas seleccionadas.',
        storageExpirationDays: 30,
        isDynamicActive: true,
        isScreenCoverVisible: true,
        showPassUserData: false,
        showPassQr: true,
        showPassLocationNote: true,
        additionalInstructions: 'Presenta este cupón digital en cualquier sucursal de Optimo Ópticas para validar tu beneficio.',
        termsAndConditionsText: '',
        privacyPolicyText: ''
    });

    useEffect(() => {
        if (isOpen) {
            loadConfig();
        }
    }, [isOpen]);

    const loadConfig = async () => {
        setIsLoading(true);
        try {
            const res = await GetAdminConfig();
            if (res && res.success && res.data) {
                const d = res.data;
                let formattedDate = '';
                if (d.matchDate) {
                    try {
                        const parsed = new Date(d.matchDate);
                        if (!isNaN(parsed.getTime())) {
                            formattedDate = parsed.toISOString().slice(0, 16);
                        }
                    } catch (e) {
                        formattedDate = '';
                    }
                }

                setFormData({
                    matchTitle: d.matchTitle || 'Atlético de San Luis vs Rival',
                    matchDate: formattedDate,
                    campaignTitle: d.campaignTitle || 'Rasca y Gana Optimo Ópticas',
                    winnerMode: d.winnerMode === 'ALL' || d.winnerMode === 'UNLIMITED' ? 'ALL' : 'LIMITED',
                    maxWinners: d.maxWinners !== undefined ? d.maxWinners : 50,
                    couponPrefix: d.couponPrefix || 'OPTI',
                    discountDescription: d.discountDescription || 'Cupón válido por descuento especial en armazones y micas seleccionadas.',
                    storageExpirationDays: d.storageExpirationDays !== undefined ? d.storageExpirationDays : 30,
                    isDynamicActive: d.isDynamicActive !== undefined ? Boolean(d.isDynamicActive) : true,
                    isScreenCoverVisible: d.isScreenCoverVisible !== undefined ? Boolean(d.isScreenCoverVisible) : true,
                    showPassUserData: Boolean(d.showPassUserData),
                    showPassQr: d.showPassQr !== undefined ? Boolean(d.showPassQr) : true,
                    showPassLocationNote: d.showPassLocationNote !== undefined ? Boolean(d.showPassLocationNote) : true,
                    additionalInstructions: d.additionalInstructions || 'Presenta este cupón digital en cualquier sucursal de Optimo Ópticas para validar tu beneficio.',
                    termsAndConditionsText: d.termsAndConditionsText || '',
                    privacyPolicyText: d.privacyPolicyText || ''
                });
            }
        } catch (error) {
            console.error("Error al cargar configuración de Optimo Ópticas:", error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al cargar la configuración de la dinámica.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.matchTitle.trim()) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'El título del partido o evento es obligatorio.' });
            setActiveTab('campana');
            return;
        }

        if (!formData.couponPrefix.trim()) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'El prefijo del folio de cupón es obligatorio (ej: OPTI).' });
            setActiveTab('campana');
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                matchTitle: formData.matchTitle.trim(),
                couponPrefix: formData.couponPrefix.trim().toUpperCase(),
                campaignTitle: formData.campaignTitle.trim(),
                maxWinners: parseInt(formData.maxWinners, 10) || 50,
                storageExpirationDays: parseInt(formData.storageExpirationDays, 10) > 0 ? parseInt(formData.storageExpirationDays, 10) : 30,
                matchDate: formData.matchDate ? new Date(formData.matchDate).toISOString() : null
            };

            const res = await UpdateAdminConfig(payload);
            if (res && res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: '¡Configuración de Optimo Ópticas guardada con éxito!'
                });
                if (onConfigUpdated) onConfigUpdated(res.data || payload);
                onClose();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res?.message || 'Error al persistir la configuración en el servidor.'
                });
            }
        } catch (error) {
            console.error("Error al actualizar configuración:", error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al actualizar la configuración.'
            });
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div 
            className="modal fade show d-block" 
            tabIndex="-1" 
            style={{ backgroundColor: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(5px)', zIndex: 1060 }}
            onClick={(e) => { if (e.target === e.currentTarget && !isSaving) onClose(); }}
        >
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content shadow-lg border-0 rounded-4 overflow-hidden bg-white text-dark">
                    {/* Header */}
                    <div className="modal-header border-bottom px-4 py-3 bg-white">
                        <div className="d-flex align-items-center gap-2.5">
                            <div className="bg-danger text-white rounded-3 p-2 d-flex align-items-center justify-content-center" style={{ width: '38px', height: '38px' }}>
                                <i className="bi bi-gear-fill fs-5"></i>
                            </div>
                            <div>
                                <h5 className="modal-title fw-bold text-dark mb-0">Configuración de la Dinámica</h5>
                                <span className="small text-muted">Rasca y Gana — Optimo Ópticas</span>
                            </div>
                        </div>
                        <button 
                            type="button" 
                            className="btn-close" 
                            aria-label="Close"
                            disabled={isSaving}
                            onClick={onClose}
                        ></button>
                    </div>

                    {/* Navegación por pestañas */}
                    <div className="px-4 pt-3 bg-light border-bottom">
                        <ul className="nav nav-tabs border-0 gap-2">
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-semibold border-0 rounded-top px-3 py-2 ${
                                        activeTab === 'campana'
                                            ? 'active bg-white text-danger border-bottom border-2 border-danger shadow-sm'
                                            : 'text-secondary bg-transparent'
                                    }`}
                                    onClick={() => setActiveTab('campana')}
                                >
                                    <i className="bi bi-sliders me-1.5"></i>
                                    Parámetros de Campaña
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-semibold border-0 rounded-top px-3 py-2 ${
                                        activeTab === 'visualizacion'
                                            ? 'active bg-white text-danger border-bottom border-2 border-danger shadow-sm'
                                            : 'text-secondary bg-transparent'
                                    }`}
                                    onClick={() => setActiveTab('visualizacion')}
                                >
                                    <i className="bi bi-ticket-perforated me-1.5"></i>
                                    Visualización del Pase
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-semibold border-0 rounded-top px-3 py-2 ${
                                        activeTab === 'legales'
                                            ? 'active bg-white text-danger border-bottom border-2 border-danger shadow-sm'
                                            : 'text-secondary bg-transparent'
                                    }`}
                                    onClick={() => setActiveTab('legales')}
                                >
                                    <i className="bi bi-shield-check me-1.5"></i>
                                    Textos Legales
                                </button>
                            </li>
                        </ul>
                    </div>

                    {/* Body */}
                    <div className="modal-body p-4 bg-light">
                        {isLoading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-danger mb-3" role="status"></div>
                                <p className="text-secondary small mb-0">Cargando parámetros de configuración...</p>
                            </div>
                        ) : (
                            <form id="optimoConfigForm" onSubmit={handleSubmit}>
                                {/* PESTAÑA 1: PARÁMETROS DE CAMPAÑA */}
                                {activeTab === 'campana' && (
                                    <div className="d-flex flex-column gap-3">
                                        <div className="card border-0 shadow-sm p-4 bg-white rounded-3">
                                            <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
                                                <i className="bi bi-megaphone-fill text-danger"></i>
                                                Información General del Partido y Campaña
                                            </h6>

                                            <div className="row g-3">
                                                <div className="col-12 col-md-7">
                                                    <label className="form-label fw-semibold text-secondary small mb-1">
                                                        Título del Partido / Evento <span className="text-danger">*</span>
                                                    </label>
                                                    <input 
                                                        type="text" 
                                                        className="form-control" 
                                                        name="matchTitle" 
                                                        placeholder="Ej: Atlético de San Luis vs Tigres"
                                                        value={formData.matchTitle} 
                                                        onChange={handleChange}
                                                        required 
                                                    />
                                                </div>

                                                <div className="col-12 col-md-5">
                                                    <label className="form-label fw-semibold text-secondary small mb-1">
                                                        Fecha y Hora del Partido
                                                    </label>
                                                    <input 
                                                        type="datetime-local" 
                                                        className="form-control" 
                                                        name="matchDate" 
                                                        value={formData.matchDate} 
                                                        onChange={handleChange} 
                                                    />
                                                </div>

                                                <div className="col-12">
                                                    <label className="form-label fw-semibold text-secondary small mb-1">
                                                        Título Comercial de la Campaña
                                                    </label>
                                                    <input 
                                                        type="text" 
                                                        className="form-control" 
                                                        name="campaignTitle" 
                                                        placeholder="Rasca y Gana Optimo Ópticas"
                                                        value={formData.campaignTitle} 
                                                        onChange={handleChange} 
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="card border-0 shadow-sm p-4 bg-white rounded-3">
                                            <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
                                                <i className="bi bi-gift-fill text-danger"></i>
                                                Reglas de Ganadores y Cupones
                                            </h6>

                                            <div className="row g-3">
                                                <div className="col-12 col-md-6">
                                                    <label className="form-label fw-semibold text-secondary small mb-1">
                                                        Modo de Entrega de Premios <span className="text-danger">*</span>
                                                    </label>
                                                    <select 
                                                        className="form-select" 
                                                        name="winnerMode" 
                                                        value={formData.winnerMode} 
                                                        onChange={handleChange}
                                                    >
                                                        <option value="LIMITED">Cupo Limitado (Stock hasta agotar)</option>
                                                        <option value="ALL">Garantizado para Todos (Ilimitado)</option>
                                                    </select>
                                                    <small className="text-muted d-block mt-1" style={{ fontSize: '0.75rem' }}>
                                                        {formData.winnerMode === 'LIMITED' 
                                                            ? 'Solo los primeros N participantes obtendrán folio ganador.' 
                                                            : 'Todos los registros válidos recibirán un cupón de descuento.'}
                                                    </small>
                                                </div>

                                                <div className="col-12 col-md-6">
                                                    <label className="form-label fw-semibold text-secondary small mb-1">
                                                        Máximo de Ganadores (Stock)
                                                    </label>
                                                    <input 
                                                        type="number" 
                                                        className="form-control" 
                                                        name="maxWinners" 
                                                        min="1" 
                                                        max="10000"
                                                        value={formData.maxWinners} 
                                                        onChange={handleChange}
                                                        disabled={formData.winnerMode === 'ALL'}
                                                    />
                                                    <small className="text-muted d-block mt-1" style={{ fontSize: '0.75rem' }}>
                                                        Aplica únicamente en modalidad de Cupo Limitado.
                                                    </small>
                                                </div>

                                                <div className="col-12 col-md-6">
                                                    <label className="form-label fw-semibold text-secondary small mb-1">
                                                        Prefijo de Folio <span className="text-danger">*</span>
                                                    </label>
                                                    <input 
                                                        type="text" 
                                                        className="form-control font-monospace text-uppercase" 
                                                        name="couponPrefix" 
                                                        maxLength="10"
                                                        placeholder="OPTI"
                                                        value={formData.couponPrefix} 
                                                        onChange={handleChange}
                                                        required 
                                                    />
                                                    <small className="text-muted d-block mt-1" style={{ fontSize: '0.75rem' }}>
                                                        Los folios generados iniciarán con #{formData.couponPrefix || 'OPTI'}-XXXX.
                                                    </small>
                                                </div>

                                                <div className="col-12 col-md-6">
                                                    <label className="form-label fw-semibold text-secondary small mb-1">
                                                        Vigencia Local / Purga TTL (Días)
                                                    </label>
                                                    <input 
                                                        type="number" 
                                                        className="form-control" 
                                                        name="storageExpirationDays" 
                                                        min="1" 
                                                        max="365"
                                                        value={formData.storageExpirationDays} 
                                                        onChange={handleChange} 
                                                    />
                                                    <small className="text-muted d-block mt-1" style={{ fontSize: '0.75rem' }}>
                                                        Días que el cupón permanecerá en la cartera del aficionado en localStorage.
                                                    </small>
                                                </div>

                                                <div className="col-12">
                                                    <label className="form-label fw-semibold text-secondary small mb-1">
                                                        Beneficio Promocional (Descripción del Cupón)
                                                    </label>
                                                    <input 
                                                        type="text" 
                                                        className="form-control" 
                                                        name="discountDescription" 
                                                        placeholder="Cupón válido por descuento especial en armazones y micas seleccionadas."
                                                        value={formData.discountDescription} 
                                                        onChange={handleChange} 
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* PESTAÑA 2: VISUALIZACIÓN DEL PASE */}
                                {activeTab === 'visualizacion' && (
                                    <div className="d-flex flex-column gap-3">
                                        <div className="card border-0 shadow-sm p-4 bg-white rounded-3">
                                            <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
                                                <i className="bi bi-eye-fill text-danger"></i>
                                                Elementos Visibles en el Cupón Digital
                                            </h6>

                                            <div className="d-flex flex-column gap-3">
                                                <div className="form-check form-switch ps-0 d-flex justify-content-between align-items-center py-2 border-bottom">
                                                    <div>
                                                        <label className="form-check-label fw-semibold text-dark d-block" htmlFor="switchUserData">
                                                            Mostrar Datos Personales del Aficionado
                                                        </label>
                                                        <small className="text-muted">
                                                            Muestra el nombre completo y teléfono enmascarado en la tarjeta digital.
                                                        </small>
                                                    </div>
                                                    <input 
                                                        className="form-check-input ms-3 fs-5" 
                                                        type="checkbox" 
                                                        role="switch"
                                                        id="switchUserData"
                                                        name="showPassUserData" 
                                                        checked={formData.showPassUserData} 
                                                        onChange={handleChange} 
                                                    />
                                                </div>

                                                <div className="form-check form-switch ps-0 d-flex justify-content-between align-items-center py-2 border-bottom">
                                                    <div>
                                                        <label className="form-check-label fw-semibold text-dark d-block" htmlFor="switchQr">
                                                            Mostrar Código QR de Canje
                                                        </label>
                                                        <small className="text-muted">
                                                            Genera el código QR de alta densidad para lectura óptica con pistola en sucursal.
                                                        </small>
                                                    </div>
                                                    <input 
                                                        className="form-check-input ms-3 fs-5" 
                                                        type="checkbox" 
                                                        role="switch"
                                                        id="switchQr"
                                                        name="showPassQr" 
                                                        checked={formData.showPassQr} 
                                                        onChange={handleChange} 
                                                    />
                                                </div>

                                                <div className="form-check form-switch ps-0 d-flex justify-content-between align-items-center py-2 border-bottom">
                                                    <div>
                                                        <label className="form-check-label fw-semibold text-dark d-block" htmlFor="switchLocation">
                                                            Mostrar Nota de Sucursales Físicas
                                                        </label>
                                                        <small className="text-muted">
                                                            Indica la leyenda sobre redención directa en sucursales de Optimo Ópticas.
                                                        </small>
                                                    </div>
                                                    <input 
                                                        className="form-check-input ms-3 fs-5" 
                                                        type="checkbox" 
                                                        role="switch"
                                                        id="switchLocation"
                                                        name="showPassLocationNote" 
                                                        checked={formData.showPassLocationNote} 
                                                        onChange={handleChange} 
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="card border-0 shadow-sm p-4 bg-white rounded-3">
                                            <h6 className="fw-bold text-dark mb-2 d-flex align-items-center gap-2">
                                                <i className="bi bi-chat-left-text-fill text-danger"></i>
                                                Instrucciones Adicionales al Aficionado
                                            </h6>
                                            <p className="text-secondary small mb-2">
                                                Texto explicativo visible al pie de la tarjeta ganadora o en el modal de canje.
                                            </p>
                                            <textarea 
                                                className="form-control" 
                                                rows="3" 
                                                name="additionalInstructions" 
                                                value={formData.additionalInstructions} 
                                                onChange={handleChange} 
                                                placeholder="Presenta este cupón digital en cualquier sucursal de Optimo Ópticas para validar tu beneficio."
                                            ></textarea>
                                        </div>
                                    </div>
                                )}

                                {/* PESTAÑA 3: TEXTOS LEGALES (T&C Y PRIVACIDAD) */}
                                {activeTab === 'legales' && (
                                    <div className="d-flex flex-column gap-3">
                                        <div className="card border-0 shadow-sm p-4 bg-white rounded-3">
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                                                    <i className="bi bi-file-text-fill text-danger"></i>
                                                    Términos y Condiciones Oficiales
                                                </h6>
                                                <span className="badge bg-danger-subtle text-danger small fw-semibold">
                                                    Módulo Legal
                                                </span>
                                            </div>
                                            <p className="text-secondary small mb-2">
                                                Especifica las bases de la promoción, vigencia de canje, restricciones por usuario y cobertura geográfica:
                                            </p>
                                            <textarea 
                                                className="form-control" 
                                                rows="6" 
                                                name="termsAndConditionsText" 
                                                value={formData.termsAndConditionsText} 
                                                onChange={handleChange} 
                                                placeholder="1. Promoción válida exclusivamente durante la jornada actual en San Luis Potosí..."
                                            ></textarea>
                                        </div>

                                        <div className="card border-0 shadow-sm p-4 bg-white rounded-3">
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                                                    <i className="bi bi-shield-lock-fill text-danger"></i>
                                                    Aviso de Privacidad (Cumplimiento LFPDPPP)
                                                </h6>
                                                <span className="badge bg-success-subtle text-success small fw-semibold">
                                                    LFPDPPP
                                                </span>
                                            </div>
                                            <p className="text-secondary small mb-2">
                                                Declara el tratamiento de datos personales conforme a la legislación mexicana para fines promocionales y mercadológicos:
                                            </p>
                                            <textarea 
                                                className="form-control" 
                                                rows="6" 
                                                name="privacyPolicyText" 
                                                value={formData.privacyPolicyText} 
                                                onChange={handleChange} 
                                                placeholder="Los datos personales recabados serán tratados conforme a la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP)..."
                                            ></textarea>
                                        </div>
                                    </div>
                                )}
                            </form>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="modal-footer bg-white border-top px-4 py-3 d-flex justify-content-between">
                        <button 
                            type="button" 
                            className="btn btn-outline-secondary px-4 fw-semibold"
                            onClick={onClose}
                            disabled={isSaving}
                        >
                            Cancelar
                        </button>
                        <button 
                            type="submit" 
                            form="optimoConfigForm"
                            className="btn btn-danger px-4 fw-bold d-flex align-items-center gap-2"
                            disabled={isSaving || isLoading}
                        >
                            {isSaving ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status"></span>
                                    <span>Guardando cambios...</span>
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-check2-circle fs-5"></i>
                                    <span>Guardar Configuración</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default OptimoOpticasConfigModal;
